import { useCallback } from 'react'
import type { PaymentSettingsInput } from '../../types/plans'

interface PaymentSettingsFieldProps {
  value: PaymentSettingsInput | null | undefined
  onChange: (value: PaymentSettingsInput | null) => void
  totalAmountRub?: number | null
  /** Если задана скидка за наличные — подсвечиваем блок спецусловий */
  discountPercent?: number | null
}

const DEFAULT_PAYMENT: PaymentSettingsInput = {
  providers: { yookassa: false, yandex_pay: false },
  yandex_pay_split: false,
  cash_special: false,
  partial: null,
}

export function PaymentSettingsField({ value, onChange, totalAmountRub, discountPercent }: PaymentSettingsFieldProps) {
  const settings = value ?? DEFAULT_PAYMENT

  const update = useCallback(
    (patch: Partial<PaymentSettingsInput>) => {
      onChange({ ...settings, ...patch })
    },
    [settings, onChange],
  )

  const anyProviderEnabled = settings.providers.yookassa || settings.providers.yandex_pay
  const hasCashDiscount = (discountPercent ?? 0) > 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>Приём онлайн-оплаты</div>

      {/* YooKassa */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={settings.providers.yookassa}
          onChange={(e) =>
            update({
              providers: { ...settings.providers, yookassa: e.target.checked },
            })
          }
          style={{ marginTop: 2 }}
        />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>YooKassa</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 1 }}>
            Пациент платит картой, СБП или SberPay
          </div>
        </div>
      </label>

      {/* Yandex Pay */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={settings.providers.yandex_pay}
          onChange={(e) =>
            update({
              providers: { ...settings.providers, yandex_pay: e.target.checked },
              yandex_pay_split: e.target.checked ? settings.yandex_pay_split : false,
            })
          }
          style={{ marginTop: 2 }}
        />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Yandex Pay</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 1 }}>
            Оплата картой через Яндекс ID; включите Split — пациент сможет разбить на 4 платежа без
            процентов
          </div>
        </div>
      </label>

      {/* Yandex Split (nested under Yandex Pay) */}
      {settings.providers.yandex_pay && (
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            cursor: 'pointer',
            marginLeft: 24,
          }}
        >
          <input
            type="checkbox"
            checked={settings.yandex_pay_split}
            onChange={(e) => update({ yandex_pay_split: e.target.checked })}
            style={{ marginTop: 2 }}
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Включить Yandex Split</div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 1 }}>
              Пациент сможет разбить сумму на 4 платежа без процентов
            </div>
          </div>
        </label>
      )}

      {/* Cash special — visible regardless of provider selection */}
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          cursor: 'pointer',
          padding: '8px 10px',
          borderRadius: 8,
          border: settings.cash_special
            ? '1.5px solid #f59e0b'
            : hasCashDiscount
              ? '1.5px dashed #fbbf24'
              : '1.5px solid transparent',
          background: settings.cash_special
            ? '#fffbeb'
            : hasCashDiscount
              ? '#fffdf0'
              : 'transparent',
          transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        <input
          type="checkbox"
          checked={settings.cash_special}
          onChange={(e) => update({ cash_special: e.target.checked })}
          style={{ marginTop: 2, accentColor: '#f59e0b' }}
        />
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: settings.cash_special || hasCashDiscount ? '#92400e' : '#374151' }}>
            Спецусловия за наличные
            {hasCashDiscount && !settings.cash_special && (
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: '#d97706', background: '#fef3c7', borderRadius: 4, padding: '1px 6px' }}>
                задана скидка {discountPercent}%
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
            За наличные — спец. условия — менеджер увидит предупреждение в журнале ссылок
          </div>
        </div>
      </label>

      {settings.cash_special && (
        <div
          style={{
            background: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 13,
            color: '#92400e',
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
          <div>
            <strong>Внимание менеджеру:</strong> пациент видит ссылку, но указано «за наличные» —
            проверьте условия перед оплатой.
          </div>
        </div>
      )}

      {/* Partial payment */}
      {anyProviderEnabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.partial !== null && settings.partial?.enabled}
              onChange={(e) =>
                update({
                  partial: e.target.checked
                    ? { enabled: true, min_rub: null, max_rub: null, presets: [] }
                    : null,
                })
              }
            />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Разрешить частичную оплату</span>
          </label>

          {settings.partial?.enabled && (
            <div
              style={{
                marginLeft: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                background: '#f8f9fa',
                borderRadius: 6,
                padding: '10px 12px',
              }}
            >
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 3 }}>
                    Минимум (₽)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={settings.partial.min_rub ?? ''}
                    placeholder="Без ограничений"
                    onChange={(e) =>
                      update({
                        partial: {
                          ...settings.partial!,
                          min_rub: e.target.value === '' ? null : Number(e.target.value),
                        },
                      })
                    }
                    style={{
                      width: '100%',
                      padding: '5px 8px',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      fontSize: 13,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 3 }}>
                    Максимум (₽)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={settings.partial.max_rub ?? ''}
                    placeholder={totalAmountRub ? String(totalAmountRub) : 'Без ограничений'}
                    onChange={(e) =>
                      update({
                        partial: {
                          ...settings.partial!,
                          max_rub: e.target.value === '' ? null : Number(e.target.value),
                        },
                      })
                    }
                    style={{
                      width: '100%',
                      padding: '5px 8px',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      fontSize: 13,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                  Пресеты на странице пациента:
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {(['10', '25', '30', '50', '75', '100'] as const).map((p) => (
                    <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={settings.partial?.presets?.includes(p) ?? false}
                        onChange={(e) => {
                          const order = ['10', '25', '30', '50', '75', '100']
                          const current = settings.partial?.presets ?? []
                          const next = (e.target.checked
                            ? [...current, p].filter((x, i, a) => a.indexOf(x) === i)
                            : current.filter((x) => x !== p)
                          ).sort((a, b) => order.indexOf(a) - order.indexOf(b)) as Array<'10' | '25' | '30' | '50' | '75' | '100'>
                          update({ partial: { ...settings.partial!, presets: next } })
                        }}
                      />
                      {p}%
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#888' }}>
                Пациент видит выбранные кнопки % от суммы плана, а также поле для произвольной суммы
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
